import styled from "styled-components";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { Image } from "../../components/Image";
import { Paragraph } from "../../components/Paragraph";
import { IndividualStatus } from "../../context/MandateStatusContext";
import { IndividualCheckResponse } from "../../types/Individual";
import success from "../../assets/card-success-icon.svg";
import inProgress from "../../assets/card-indeterminate-icon-gray.svg";
import inReview from "../../assets/card-in-review-icon.svg";

/**
 * One compact entry per check field (status icon, name, then the result on
 * its own line), wrapping into as many columns as fit. Replaces the
 * four-column grey grid, which took a lot of height for little information.
 * The icons are the same ones the rest of the Portal uses for check status.
 */
const FieldsCard = styled.div`
  ${({ theme: { colors, radii, space } }) => `
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: ${space[2]} ${space[3]};
    margin: 0 ${space[4]} ${space[3]};
    padding: ${space[2]} ${space[3]};
    background: ${colors.white};
    border: 1px solid ${colors.gray[100]};
    border-radius: ${radii[1]};
  `}
`;

const statusIcon = (status?: string) =>
  status === IndividualStatus.Accepted
    ? success
    : status === IndividualStatus.Rejected
    ? inReview
    : inProgress;

type Props = {
  individual_checkfield_result: IndividualCheckResponse["individual_checkfield_result"];
};

export const CheckFields = (props: Props) => {
  const { individual_checkfield_result } = props;

  const fields =
    individual_checkfield_result?.filter(
      (checkfield) =>
        checkfield?.check_field?.slug_name !== "latitude" &&
        checkfield?.check_field?.slug_name !== "longitude"
    ) ?? [];

  if (fields.length === 0) return null;

  return (
    <FieldsCard>
      {fields.map((checkfield, i) => (
        <Flex key={i} gap="8" alignItems="flex-start" minWidth={0}>
          <Image
            src={statusIcon(checkfield?.status?.status)}
            alt="status icon"
            height="20px"
            width="20px"
            flexShrink={0}
          />
          <Box minWidth={0}>
            <Paragraph m="0px" fontSize={1} fontWeight="600" color="gray.80">
              {checkfield?.check_field?.name}
            </Paragraph>
            {checkfield?.result && (
              <Paragraph m="0px" fontSize={1} color="gray.60">
                {checkfield.result}
              </Paragraph>
            )}
          </Box>
        </Flex>
      ))}
    </FieldsCard>
  );
};
